/* eslint-disable no-console */
import { Socket } from 'socket.io-client';

import { authorizationForSession, getAccessToken, renewAccessToken } from 'utils/authSession';

export interface SocketResProps {
  message: string;
  status: boolean;
}

type SocketAuthCallback = (data: { authorization: string }) => void;

/**
 * Reads the session's current token on every handshake, socket.io's own reconnects included, so a
 * token renewal needs no reconnect and a reconnect never presents a renewed-away token (LMD-682).
 * `accessToken` is the fallback while the store holds none.
 */
const sessionAuth = (accessToken: string) => (callback: SocketAuthCallback) => {
  const token = getAccessToken() || accessToken;
  callback({ authorization: token ? authorizationForSession(token) : '' });
};

export function socketInit(socket: Socket, accessToken: string) {
  if (socket.connected) {
    return;
  }
  socket.auth = sessionAuth(accessToken);
  socket.connect();
}
export function socketClose(socket: Socket) {
  socket.auth = { authorization: '' };
  socket.disconnect();
}

const RECONNECT_COOLDOWN_MS = 30_000;
const lastReconnectAt = new WeakMap<Socket, number>();

/**
 * Renews through the shared session so the new pair reaches the store and every other caller,
 * instead of a private renewal whose token only this socket ever saw. The cooldown stops a
 * connect_error -> renew -> connect_error loop when the handshake keeps failing. The socket hooks
 * key on the session, not the token, so storing the renewed token does not reconnect a second time.
 */
export async function socketReconnect(socket: Socket) {
  const lastAttemptAt = lastReconnectAt.get(socket) ?? 0;
  if (Date.now() - lastAttemptAt < RECONNECT_COOLDOWN_MS) {
    return console.warn('Socket reconnect skipped: a renewal was attempted too recently');
  }
  lastReconnectAt.set(socket, Date.now());

  const accessToken = await renewAccessToken();
  if (!accessToken) {
    return console.warn('Socket reconnect skipped: token renewal failed');
  }
  socketClose(socket);
  socketInit(socket, accessToken);
}

export const errorCallBack = (event: string, res: SocketResProps | Error) => {
  if ('status' in res && res.status) {
    console.log(`socket ${event} => ${res.message}`);
  } else {
    console.error(`socket ${event} error => ${res.message}`);
  }
};
